import { loadComponent } from '@/utils/component-map'
import request from '@/api/request'

/**
 * 检查路径是否有权限（支持精确匹配和特殊多入口页面）
 * @param {string} path - 要检查的路径
 * @param {string[]} permissions - 用户权限列表
 * @returns {boolean}
 */
function checkPathPermission(path, permissions) {
  if (!path || !permissions || permissions.length === 0) {
    return false
  }

  // 超级管理员权限
  if (permissions.includes('/*')) {
    return true
  }

  // 精确匹配
  if (permissions.includes(path)) {
    return true
  }

  // 特殊处理：多入口页面权限检查
  // 管理中心客户详情页支持从订单页面或客户管理页面进入
  if (path === '/manage/customers/:id') {
    return permissions.includes('/manage/customers') || permissions.includes('/manage/orders')
  }

  // 作业中心客户详情页支持从我的任务或我的客户进入
  if (path === '/work/clients/:id') {
    return permissions.includes('/work/clients') || permissions.includes('/work/tasks')
  }

  return false
}

/**
 * 将菜单列表转换为路由配置
 * @param {Array} menuList - 菜单列表
 * @returns {Array} 路由配置
 */
function convertMenusToRoutes(menuList) {
  if (!menuList || menuList.length === 0) return []

  return menuList.map(menu => ({
    path: menu.path,
    name: menu.name ? menu.name.replace(/\//g, '_') : undefined,
    component: loadComponent(menu.component),
    meta: {
      title: menu.title,
      icon: menu.icon,
      isShow: menu.is_show
    }
  }))
}

export default {
  namespaced: true,

  state: {
    // 动态路由配置（用于 router.addRoutes）
    dynamicRoutes: [],
    // 侧边栏菜单（树形，用于显示）
    sidebarRouters: [],
    // 所有菜单列表（扁平化，用于管理）
    menuList: [],
    // 是否已经加载过菜单（防止死循环）
    hasLoaded: false
  },

  mutations: {
    // 设置动态路由
    setDynamicRoutes(state, routes) {
      state.dynamicRoutes = routes
    },

    // 设置侧边栏菜单（树形）
    setSidebarRouters(state, routes) {
      state.sidebarRouters = routes
    },

    // 设置菜单列表（扁平化）
    setMenuList(state, list) {
      state.menuList = list
    },

    // 设置加载状态
    setHasLoaded(state, value) {
      state.hasLoaded = value
    }
  },

  actions: {
    /**
     * 从已持久化的菜单列表重新生成路由（刷新页面后使用）
     * 不需要请求 API，直接从 menuList 恢复
     */
    restoreRoutesFromCache({ state, commit }) {
      const menuList = state.menuList
      if (!menuList || menuList.length === 0) {
        return []
      }

      // 从 menuList 重新生成路由（组件函数需要重新加载）
      const routes = convertMenusToRoutes(menuList)
      commit('setDynamicRoutes', routes)
      
      return routes
    },

    /**
     * 获取当前用户的菜单和权限
     * 1. 获取所有菜单和权限列表
     * 2. 根据权限过滤菜单
     * 3. 转换为路由配置
     * 4. 构建树形菜单
     */
    async fetchUserMenus({ commit, state }, forceRefresh = false) {
      // 如果已经加载过，不再重复加载（除非强制刷新）
      if (state.hasLoaded && !forceRefresh) {
        return state.dynamicRoutes
      }

      try {
        const res = await request.get('/permissions/menus/user-menus')
        const { menus, permissions } = res.data

        // 1. 根据权限过滤菜单（只保留有权限的菜单）
        // 超管权限为 "/*"，直接返回所有菜单不做过滤
        const isSuperAdmin = permissions.includes('/*')
        const filteredMenus = isSuperAdmin ? menus : menus.filter(menu =>
          checkPathPermission(menu.path, permissions)
        )

        // 特别检查客户详情页
        const customerDetailMenu = filteredMenus.find(m => m.path.includes('/clients/') || m.path.includes('/customers/:id'))

        // 2. 转换为路由配置
        // 包括所有有权限的菜单，包括隐藏的（如客户详情页）
        const routes = convertMenusToRoutes(filteredMenus)

        // 3. 构建树形菜单（用于侧边栏显示）- 只显示 is_show=1 的菜单
        const treeMenus = buildMenuTree(menus, 0, permissions)

        commit('setDynamicRoutes', routes)
        commit('setSidebarRouters', treeMenus)
        commit('setMenuList', filteredMenus)
        commit('setHasLoaded', true)

        return routes
      } catch (error) {
        console.error('获取用户菜单失败:', error)
        commit('setHasLoaded', true)
        throw error
      }
    },

    /**
     * 获取所有菜单（用于菜单管理页面）
     */
    async fetchAllMenus({ commit }) {
      try {
        const res = await request.get('/permissions/menus')
        const tree = res.data || []

        // 转换为扁平列表（便于管理）
        const flatList = treeToList(tree)

        commit('setMenuList', flatList)

        return { tree, list: flatList }
      } catch (error) {
        console.error('获取菜单列表失败:', error)
        throw error
      }
    },

    /**
     * 创建菜单
     */
    async createMenu({ dispatch }, data) {
      await request.post('/permissions/menus', data)
      // 重新加载菜单列表
      return dispatch('fetchAllMenus')
    },

    /**
     * 更新菜单
     */
    async updateMenu({ dispatch }, { id, data }) {
      await request.put(`/permissions/menus/${id}`, data)
      return dispatch('fetchAllMenus')
    },

    /**
     * 删除菜单
     */
    async deleteMenu({ dispatch }, id) {
      await request.delete(`/permissions/menus/${id}`)
      return dispatch('fetchAllMenus')
    },

    /**
     * 批量更新排序
     */
    async sortMenus({ dispatch }, sorts) {
      await request.post('/permissions/menus/sort', { sorts })
      return dispatch('fetchAllMenus')
    },

    /**
     * 获取角色的菜单
     */
    async getRoleMenus({ }, roleId) {
      const res = await request.get(`/permissions/roles/${roleId}/menus`)
      return res.data || []
    },

    /**
     * 给角色分配菜单
     */
    async assignMenusToRole({ }, { roleId, menuIds }) {
      await request.post(`/permissions/roles/${roleId}/menus`, { menuIds })
    }
  }
}

/**
 * 将扁平菜单列表构建为树形结构（仅用于侧边栏显示，只包含 is_show=1 的菜单）
 * @param {Array} menus - 所有菜单列表
 * @param {number} parentId - 父菜单ID
 * @param {Array} permissions - 用户权限列表
 */
function buildMenuTree(menus, parentId = 0, permissions = []) {
  // 如果是超级管理员，显示所有
  const isSuperAdmin = permissions.includes('/*')
  
  const result = menus
    .filter(menu => {
      // 只处理当前父ID下的、需要显示的菜单
      if (menu.parent_id !== parentId || menu.is_show !== 1) {
        return false
      }
      
      // 父菜单（有子菜单的）：递归检查是否有子菜单有权限
      const hasChildren = menus.some(m => m.parent_id === menu.id && m.is_show === 1)
      if (hasChildren) {
        const children = buildMenuTree(menus, menu.id, permissions)
        return children.length > 0
      }
      
      // 叶子菜单（无子菜单的）：检查权限
      return isSuperAdmin || permissions.includes(menu.path)
    })
    .map(menu => ({
      ...menu,
      children: buildMenuTree(menus, menu.id, permissions)
    }))
  
  // 一级菜单时，将首页始终排在最前面
  if (parentId === 0) {
    result.sort((a, b) => {
      // 首页优先级最高，始终排在第一位
      if (a.path === '/home') return -1
      if (b.path === '/home') return 1
      // 其他菜单按 sort_order 排序
      return (a.sort_order || 0) - (b.sort_order || 0)
    })
  }
  
  return result
}

/**
 * 将树形结构转换为扁平列表
 */
function treeToList(tree, parentId = 0) {
  const list = []

  tree.forEach(item => {
    const { children, ...rest } = item
    const node = { ...rest, parent_id: parentId }
    list.push(node)

    if (children && children.length > 0) {
      list.push(...treeToList(children, item.id))
    }
  })

  return list
}